import { LitElement, html, nothing } from 'lit';
import type { TemplateResult } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { widgetStyles } from './styles/widget-styles';
import { FONT_UI, defaultFontsUrl, installFonts } from './styles/fonts';
import { AudioEngine } from './audio/engine';
import { LoadError, loadModel } from './audio/loader';
import {
  DEFAULT_MODEL_ID, STRENGTH_COLOR, displayOrder, getModel, isEnabled, menuSections,
  type ModelDef,
} from './state/models';
import {
  defaultUi, isDimmed, sourceGains, withGain, withMuteToggle, withSoloToggle, withStrength,
  type MixUi,
} from './state/mix';
import {
  DB_DEFAULT, clampDb, dbToLinear, linearToDb, parseDb, parsePercent, strengthToPercent,
} from './state/gain';
import { WaveformRenderer, type WaveScene } from './ui/waveform';
import { RulerCanvas } from './ui/ruler';
import { LevelMeterCanvas } from './ui/meter';
import { renderNav } from './ui/nav';
import { renderModelPicker } from './ui/model-picker';
import { renderStemRow, valueText } from './ui/stem-row';
import { renderSpeakersRow } from './ui/speakers-row';
import { renderErrorPanel } from './ui/error-panel';
import { installKeyboardHandlers } from './a11y/keyboard';
import { emit } from './analytics/datalayer';

type Status = 'idle' | 'loading' | 'ready' | 'error';

const DEFAULT_STEMS_URL = './stems/';
const SLIDER_DEBOUNCE_MS = 500;

@customElement('photon-widget')
export class PhotonWidget extends LitElement {
  static override styles = widgetStyles;

  @property({ type: String })
  model = DEFAULT_MODEL_ID;

  @property({ type: String, attribute: 'stems-url' })
  stemsUrl = DEFAULT_STEMS_URL;

  @property({ type: String, attribute: 'fonts-url' })
  fontsUrl = defaultFontsUrl();

  @state() private status: Status = 'idle';
  @state() private currentModel: ModelDef;
  @state() private ui: MixUi;
  @state() private bypass = false;
  @state() private isPlaying = false;
  @state() private currentTimeS = 0;
  @state() private durationS = 0;
  @state() private pickerOpen = false;
  @state() private pickerHighlight: string | null = null;
  @state() private announcement = '';
  @state() private hasBeenReady = false;
  @state() private hasInteracted = false;

  @query('canvas.waveform') private waveformCanvas?: HTMLCanvasElement;
  @query('canvas.ruler') private rulerCanvas?: HTMLCanvasElement;
  @query('canvas.meter') private meterCanvas?: HTMLCanvasElement;
  @query('.wave-stack') private waveStack?: HTMLElement;
  @query('.playhead') private playheadEl?: HTMLElement;

  private engine: AudioEngine | null = null;
  private renderer: WaveformRenderer | null = null;
  private ruler: RulerCanvas | null = null;
  private meter: LevelMeterCanvas | null = null;
  private meterTailRaf: number | null = null;   // decay frames after pause
  private io: IntersectionObserver | null = null;
  private stackResize: ResizeObserver | null = null;
  private waveWidth = 0;
  private uninstallKeyboard: (() => void) | null = null;
  private engineUnsubscribers: Array<() => void> = [];
  private loadAbort: AbortController | null = null;
  private decodeStartMs = 0;
  private firstPlayEmitted = false;
  private sliderDebounceTimers = new Map<string, number>();
  // Ruler / playhead-grip drag: pointer captured on the element that was
  // pressed, seeks coalesced to one per frame (each seek restarts the sources).
  private rulerPointerId: number | null = null;
  private rulerPendingTime: number | null = null;
  private rulerRaf: number | null = null;

  constructor() {
    super();
    this.currentModel = getModel(DEFAULT_MODEL_ID);
    this.ui = defaultUi(this.currentModel);
  }

  override connectedCallback(): void {
    super.connectedCallback();
    installFonts(this.fontsUrl);
    this.currentModel = this.resolveInitialModel(this.model);
    this.ui = defaultUi(this.currentModel);
    emit('photon_widget_mount', { model: this.currentModel.id });

    this.uninstallKeyboard = installKeyboardHandlers(this, {
      onTogglePlay: () => this.togglePlay(),
    });

    // Modality tracking: form controls show :focus-visible after mouse clicks
    // in Safari (and sometimes Chrome) even though they shouldn't per spec.
    // Track whether the last input was keyboard vs. mouse and gate the focus
    // ring via CSS on data-modality.
    this.setAttribute('data-modality', 'mouse');
    document.addEventListener('keydown', this.handleKeyModality, true);
    document.addEventListener('pointerdown', this.handlePointerModality, true);

    this.setupViewportTrigger();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.uninstallKeyboard?.();
    document.removeEventListener('keydown', this.handleKeyModality, true);
    document.removeEventListener('pointerdown', this.handlePointerModality, true);
    document.removeEventListener('pointerdown', this.handleOutsidePointer, true);
    this.io?.disconnect();
    this.io = null;
    this.stackResize?.disconnect();
    this.stackResize = null;
    this.loadAbort?.abort();
    this.renderer?.dispose();
    this.renderer = null;
    this.ruler?.dispose();
    this.ruler = null;
    this.stopMeterTail();
    this.meter?.dispose();
    this.meter = null;
    for (const unsub of this.engineUnsubscribers) unsub();
    this.engineUnsubscribers = [];
    for (const t of this.sliderDebounceTimers.values()) window.clearTimeout(t);
    this.sliderDebounceTimers.clear();
    if (this.rulerRaf !== null) cancelAnimationFrame(this.rulerRaf);
    this.engine?.dispose();
    this.engine = null;
  }

  // An unknown or disabled `model` attribute falls back to the default rather
  // than breaking the embed.
  private resolveInitialModel(id: string): ModelDef {
    try {
      const m = getModel(id);
      if (isEnabled(m)) return m;
      console.warn(`[photon-widget] model "${id}" is disabled; using ${DEFAULT_MODEL_ID}`);
    } catch {
      console.warn(`[photon-widget] unknown model "${id}"; using ${DEFAULT_MODEL_ID}`);
    }
    return getModel(DEFAULT_MODEL_ID);
  }

  private handleKeyModality = (e: KeyboardEvent): void => {
    // Only navigational keys promote to keyboard modality.
    if (e.key === 'Tab' || e.key.startsWith('Arrow') || e.key === ' ' || e.key === 'Enter') {
      this.setAttribute('data-modality', 'keyboard');
    }
  };

  private handlePointerModality = (): void => {
    this.setAttribute('data-modality', 'mouse');
  };

  // Closes the model menu on any pointerdown outside it (installed only while open).
  private handleOutsidePointer = (e: PointerEvent): void => {
    if (!this.pickerOpen) return;
    const picker = this.shadowRoot?.querySelector('.picker');
    if (picker && e.composedPath().includes(picker)) return;
    this.closePicker();
  };

  override updated(changed: Map<string, unknown>): void {
    super.updated(changed);
    if (this.waveformCanvas && !this.renderer) {
      this.renderer = new WaveformRenderer(this.waveformCanvas, {
        seek: (t) => this.handleScrub(t),
      });
      this.renderer.setDuration(this.durationS);
      this.renderer.setScene(this.sceneFor());
    }
    if (this.rulerCanvas && !this.ruler) {
      this.ruler = new RulerCanvas(this.rulerCanvas, FONT_UI);
      this.ruler.setDuration(this.durationS);
    }
    if (this.meterCanvas && !this.meter) {
      this.meter = new LevelMeterCanvas(this.meterCanvas);
      this.meter.setBypass(this.bypass);
    }
    if (this.waveStack && !this.stackResize) {
      this.stackResize = new ResizeObserver((entries) => {
        this.waveWidth = entries[0]?.contentRect.width ?? this.waveWidth;
        this.updatePlayhead();
      });
      this.stackResize.observe(this.waveStack);
      this.waveWidth = this.waveStack.getBoundingClientRect().width;
    }
    if (changed.has('pickerOpen')) {
      if (this.pickerOpen) {
        document.addEventListener('pointerdown', this.handleOutsidePointer, true);
      } else {
        document.removeEventListener('pointerdown', this.handleOutsidePointer, true);
      }
    }
    this.updatePlayhead();
  }

  private setupViewportTrigger(): void {
    if (typeof IntersectionObserver === 'undefined') {
      this.startLoading();
      return;
    }
    this.io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.io?.disconnect();
            this.io = null;
            this.startLoading();
            break;
          }
        }
      },
      { rootMargin: '200px' },
    );
    this.io.observe(this);
  }

  private async startLoading(): Promise<void> {
    if (this.status === 'loading' || this.status === 'ready') return;
    return this.loadCurrentModel();
  }

  // Loads whatever this.currentModel points at. Used for both initial load and
  // model switch. Always aborts any in-flight load first and starts fresh.
  private async loadCurrentModel(): Promise<void> {
    this.loadAbort?.abort();
    const abort = new AbortController();
    this.loadAbort = abort;
    this.status = 'loading';
    this.decodeStartMs = performance.now();
    const model = this.currentModel;

    try {
      if (!this.engine) {
        this.engine = new AudioEngine();
        this.attachEngineListeners();
      }
      const loaded = await loadModel(this.engine.context, this.stemsUrl, model, abort.signal);
      // Another load may have superseded us while we awaited.
      if (abort.signal.aborted) return;
      await this.engine.setBuffers(loaded.buffers, sourceGains(model, this.ui, this.bypass));
      this.durationS = this.engine.duration;
      // Swap renderer state in one microtask: duration, peaks and scene. All
      // scheduleRender calls coalesce into a single RAF paint so the old→new
      // transition is one atomic frame — no black flash.
      this.renderer?.setDuration(this.durationS);
      this.renderer?.setPeaks(loaded.peaks);
      this.renderer?.setScene(this.sceneFor());
      this.ruler?.setDuration(this.durationS);
      emit('photon_widget_peaks_loaded', {
        model: model.id,
        bytes: loaded.peakBytes,
        parse_ms: Math.round(loaded.peakParseMs),
      });
      this.status = 'ready';
      this.hasBeenReady = true;
      this.announcement = 'Demo ready';
      const decodeMs = Math.round(performance.now() - this.decodeStartMs);
      emit('photon_widget_ready', { model: model.id, decode_ms: decodeMs });
    } catch (err) {
      if (abort.signal.aborted) return;
      const detail = err instanceof LoadError ? err.detail : String(err);
      const reason = err instanceof LoadError ? err.reason : 'unknown';
      this.status = 'error';
      this.announcement = "Couldn't load the demo";
      emit('photon_widget_error', { reason, detail });
    }
  }

  private attachEngineListeners(): void {
    if (!this.engine) return;
    this.engineUnsubscribers.push(
      this.engine.on('play', () => {
        this.isPlaying = true;
        this.stopMeterTail();   // the timeupdate ticks drive the meter again
        this.announcement = 'Playing';
        if (!this.firstPlayEmitted) {
          this.firstPlayEmitted = true;
          emit('photon_widget_play', { model: this.currentModel.id });
        }
      }),
      this.engine.on('pause', () => {
        this.isPlaying = false;
        this.startMeterTail();
        this.announcement = 'Paused';
        emit('photon_widget_pause', {
          model: this.currentModel.id,
          position_s: Number(this.currentTimeS.toFixed(3)),
        });
      }),
      this.engine.on('timeupdate', () => {
        this.currentTimeS = this.engine!.currentTime;
        this.updatePlayhead();
        if (this.engine!.isPlaying) this.tickMeter();
      }),
    );
  }

  // One meter frame: read the output peaks and advance the ballistics.
  // Returns true while the display is still moving.
  private tickMeter(): boolean {
    if (!this.engine || !this.meter) return false;
    const [l, r] = this.engine.readPeakDb();
    return this.meter.update(l, r, performance.now());
  }

  // After pause the sources are silent, so the bars decay and the peak lines
  // fall on their own ballistics; keep ticking until nothing moves.
  private startMeterTail(): void {
    if (this.meterTailRaf !== null) return;
    const tick = () => {
      this.meterTailRaf = null;
      if (this.engine?.isPlaying) return;
      if (this.tickMeter()) this.meterTailRaf = requestAnimationFrame(tick);
    };
    this.meterTailRaf = requestAnimationFrame(tick);
  }

  private stopMeterTail(): void {
    if (this.meterTailRaf !== null) {
      cancelAnimationFrame(this.meterTailRaf);
      this.meterTailRaf = null;
    }
  }

  // The playhead moves on the compositor (translateX) from the RAF tick; the
  // stack's width is cached by a ResizeObserver so this never reads layout.
  private updatePlayhead(): void {
    if (!this.playheadEl) return;
    if (this.durationS <= 0 || this.waveWidth <= 0) {
      this.playheadEl.style.opacity = '0';
      return;
    }
    const frac = Math.max(0, Math.min(1, this.currentTimeS / this.durationS));
    // Whole pixels keep the 1px line crisp; the last column at the very end.
    const x = Math.min(this.waveWidth - 1, Math.round(frac * this.waveWidth));
    this.playheadEl.style.transform = `translateX(${x}px)`;
    this.playheadEl.style.opacity = '1';
  }

  // ---- Mix state → engine + waveform -------------------------------------

  // Bypass is audible only: the waveform keeps showing the user's settings
  // (gains, mute/solo, strength) so the pressed Bypass button stays the one
  // visual cue (Gabe, Oct 27).
  private sceneFor(): WaveScene {
    const model = this.currentModel;
    const ui = this.ui;
    if (model.kind === 'demix' && ui.kind === 'demix') {
      return {
        kind: 'demix',
        layers: model.stems.map((s, i) => ({
          name: s.name,
          color: s.color,
          gain: ui.stems[i].gain,
          grey: isDimmed(ui, i),
        })),
      };
    }
    if (model.kind === 'restore' && ui.kind === 'restore') {
      return { kind: 'restore', strength: ui.strength, bypass: false };
    }
    return { kind: 'demix', layers: [] };
  }

  private applyMix(): void {
    if (this.engine && this.status === 'ready') {
      this.engine.setGains(sourceGains(this.currentModel, this.ui, this.bypass));
    }
    this.renderer?.setScene(this.sceneFor());
  }

  private setUi(next: MixUi): void {
    if (next === this.ui) return;
    this.ui = next;
    this.applyMix();
  }

  // ---- Transport -----------------------------------------------------------

  private async togglePlay(): Promise<void> {
    if (!this.engine || this.status !== 'ready') return;
    this.hasInteracted = true;
    if (this.engine.isPlaying) {
      this.engine.pause();
    } else {
      await this.engine.play();
    }
    // Release focus from any non-button element so stray focus rings from a
    // previous scrub / dropdown / slider don't linger on the canvas or other
    // focusable controls.
    const active = this.shadowRoot?.activeElement as HTMLElement | null;
    if (active && active.tagName !== 'BUTTON') {
      active.blur();
    }
  }

  private handleSeekStart = (): void => {
    if (!this.engine || this.status !== 'ready') return;
    this.engine.seekToStart();
    this.announcement = 'Seeked to start';
  };

  private handleSeekEnd = (): void => {
    if (!this.engine || this.status !== 'ready') return;
    this.engine.seekToEnd();
    this.announcement = 'Seeked to end';
  };

  private handleScrub(seconds: number): void {
    if (!this.engine || this.status !== 'ready') return;
    const from = this.currentTimeS;
    this.engine.seek(seconds);
    this.announcement = `Seeked to ${seconds.toFixed(1)} seconds`;
    emit('photon_widget_scrub', {
      model: this.currentModel.id,
      from_s: Number(from.toFixed(3)),
      to_s: Number(seconds.toFixed(3)),
    });
  }

  // Time under the pointer on the ruler band; the stack's rect is the same
  // x-origin for the ruler canvas and the playhead.
  private rulerTimeAt(clientX: number): number {
    const rect = this.waveStack?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return 0;
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return frac * this.durationS;
  }

  // Shared by the ruler canvas and the playhead grip: press seeks, dragging
  // scrubs until release (pointer capture keeps the drag alive outside the
  // element, as the app's ruler grip does).
  private handleRulerPointerDown = (e: PointerEvent): void => {
    if (this.durationS <= 0 || e.button !== 0 || this.status !== 'ready') return;
    e.preventDefault();
    this.rulerPointerId = e.pointerId;
    const target = e.currentTarget as HTMLElement;
    try { target.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
    // Closed-hand cursor only for a grip drag (the app's DraggingHandCursor);
    // a drag started on the bare ruler keeps the arrow.
    if (target.classList.contains('hit')) this.setAttribute('data-scrubbing', '');
    this.handleScrub(this.rulerTimeAt(e.clientX));
  };

  private handleRulerPointerMove = (e: PointerEvent): void => {
    if (this.rulerPointerId !== e.pointerId) return;
    this.rulerPendingTime = this.rulerTimeAt(e.clientX);
    if (this.rulerRaf !== null) return;
    this.rulerRaf = requestAnimationFrame(() => {
      this.rulerRaf = null;
      if (this.rulerPendingTime !== null) {
        this.handleScrub(this.rulerPendingTime);
        this.rulerPendingTime = null;
      }
    });
  };

  private handleRulerPointerUp = (e: PointerEvent): void => {
    if (this.rulerPointerId !== e.pointerId) return;
    this.rulerPointerId = null;
    this.removeAttribute('data-scrubbing');
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  private handleToggleBypass = (): void => {
    if (this.status !== 'ready') return;
    this.bypass = !this.bypass;
    this.applyMix();
    this.meter?.setBypass(this.bypass);
    this.announcement = this.bypass ? 'Bypass on' : 'Bypass off';
    emit('photon_widget_bypass_toggle', { model: this.currentModel.id, bypass: this.bypass });
  };

  // ---- Sliders -------------------------------------------------------------

  private emitSliderAdjust(stem: string, payload: Record<string, unknown>): void {
    const prev = this.sliderDebounceTimers.get(stem);
    if (prev) window.clearTimeout(prev);
    const timer = window.setTimeout(() => {
      emit('photon_widget_slider_adjust', { model: this.currentModel.id, stem, ...payload });
      this.sliderDebounceTimers.delete(stem);
    }, SLIDER_DEBOUNCE_MS);
    this.sliderDebounceTimers.set(stem, timer);
  }

  private setStemDb(index: number, db: number): void {
    const clamped = clampDb(db);
    this.setUi(withGain(this.ui, index, dbToLinear(clamped)));
    const stem = this.currentModel.kind === 'demix' ? this.currentModel.stems[index].name : '';
    this.emitSliderAdjust(stem, { value_db: Number(clamped.toFixed(1)) });
  }

  private setStrength(strength: number): void {
    this.setUi(withStrength(this.ui, strength));
    const next = this.ui;
    if (next.kind === 'restore') {
      this.emitSliderAdjust('strength', { value: strengthToPercent(next.strength) });
    }
  }

  private handleToggleMute(index: number): void {
    const next = withMuteToggle(this.ui, index);
    this.setUi(next);
    if (next.kind === 'demix' && this.currentModel.kind === 'demix') {
      emit('photon_widget_mute_toggle', {
        model: this.currentModel.id,
        stem: this.currentModel.stems[index].name,
        muted: next.stems[index].muted,
      });
    }
  }

  private handleToggleSolo(index: number): void {
    const next = withSoloToggle(this.ui, index);
    this.setUi(next);
    if (next.kind === 'demix' && this.currentModel.kind === 'demix') {
      emit('photon_widget_solo_toggle', {
        model: this.currentModel.id,
        stem: this.currentModel.stems[index].name,
        soloed: next.stems[index].soloed,
      });
    }
  }

  // ---- Model picker --------------------------------------------------------

  private flatMenuIds(): string[] {
    return menuSections().flatMap((s) => s.models.map((m) => m.id));
  }

  private openPicker(): void {
    this.pickerOpen = true;
    this.pickerHighlight = this.currentModel.id;
  }

  // `releaseFocus`: after a pick (or a mouse close) the button must not keep
  // focus, or the next Space is a native button press that re-opens the menu
  // instead of toggling playback. Escape keeps focus so Tab still works.
  private closePicker(releaseFocus = false): void {
    this.pickerOpen = false;
    this.pickerHighlight = null;
    if (releaseFocus) {
      const active = this.shadowRoot?.activeElement as HTMLElement | null;
      active?.blur();
    }
  }

  private handlePickerToggle = (): void => {
    if (this.pickerOpen) {
      this.closePicker(this.getAttribute('data-modality') === 'mouse');
    } else {
      this.openPicker();
    }
  };

  private handlePickerKeydown = (e: KeyboardEvent): void => {
    const ids = this.flatMenuIds();
    if (!this.pickerOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.openPicker();
      }
      return;
    }
    const idx = this.pickerHighlight ? ids.indexOf(this.pickerHighlight) : -1;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.pickerHighlight = ids[Math.min(ids.length - 1, idx + 1)];
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.pickerHighlight = ids[Math.max(0, idx - 1)];
        break;
      case 'Home':
        e.preventDefault();
        this.pickerHighlight = ids[0];
        break;
      case 'End':
        e.preventDefault();
        this.pickerHighlight = ids[ids.length - 1];
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (this.pickerHighlight) this.handleModelChange(this.pickerHighlight);
        break;
      case 'Escape':
      case 'Tab':
        this.closePicker();
        break;
      default:
        break;
    }
  };

  private handleModelChange = async (newId: string): Promise<void> => {
    this.closePicker(true);
    const prev = this.currentModel.id;
    if (newId === prev) return;
    emit('photon_widget_model_change', { from: prev, to: newId });

    // Pause current playback and reset play-tracking so the new model's first
    // play event is emitted fresh.
    this.engine?.pause();
    this.firstPlayEmitted = false;
    this.currentTimeS = 0;

    // Swap the model (panel shows the new rows at default values, disabled
    // while loading). Leave the renderer's peaks untouched so the previous
    // model's waveform remains painted until new peaks arrive. The atomic
    // swap happens at the end of loadCurrentModel.
    this.currentModel = getModel(newId);
    this.ui = defaultUi(this.currentModel);
    this.bypass = false;
    this.stopMeterTail();
    this.meter?.setBypass(false);
    this.meter?.setSilence();

    await this.loadCurrentModel();
  };

  // ---- Render --------------------------------------------------------------

  private renderRows(): TemplateResult {
    const model = this.currentModel;
    const ui = this.ui;
    const disabled = this.status !== 'ready';

    if (model.kind === 'restore') {
      const strength = ui.kind === 'restore' ? ui.strength : model.defaultStrength;
      return renderStemRow(
        {
          id: 'strength',
          title: 'Strength',
          color: STRENGTH_COLOR,
          mode: 'percent',
          value: strengthToPercent(strength),
          dimmed: false,
          disabled,
        },
        {
          onInput: (pct) => this.setStrength(pct / 100),
          onCommitText: (text) => {
            const parsed = parsePercent(text);
            if (parsed !== null) this.setStrength(parsed);
            const now = this.ui.kind === 'restore' ? this.ui.strength : strength;
            return valueText('percent', strengthToPercent(now));
          },
          onReset: () => this.setStrength(model.defaultStrength),
        },
      );
    }

    const rows = displayOrder(model.stems).map((i) => {
      const stem = model.stems[i];
      const s = ui.kind === 'demix' ? ui.stems[i] : { gain: 1, muted: false, soloed: false };
      const db = clampDb(linearToDb(s.gain));
      return renderStemRow(
        {
          id: stem.name,
          title: stem.title,
          color: stem.color,
          mode: 'dB',
          value: db,
          dimmed: !disabled && isDimmed(ui, i),
          disabled,
          sm: { soloed: s.soloed, muted: s.muted },
        },
        {
          onInput: (value) => this.setStemDb(i, value),
          onCommitText: (text) => {
            const parsed = parseDb(text);
            if (parsed !== null) this.setStemDb(i, parsed);
            const now = this.ui.kind === 'demix' ? this.ui.stems[i].gain : s.gain;
            return valueText('dB', clampDb(linearToDb(now)));
          },
          onReset: () => this.setStemDb(i, DB_DEFAULT),
          onSolo: () => this.handleToggleSolo(i),
          onMute: () => this.handleToggleMute(i),
        },
      );
    });

    return html`
      ${model.speakers ? renderSpeakersRow(model.speakers) : nothing}
      ${rows}
    `;
  }

  override render(): TemplateResult {
    const duration = this.durationS;
    const transportDisabled = this.status !== 'ready';
    const showLoadingOverlay = this.status === 'loading';

    const nav = renderNav(
      {
        isPlaying: this.isPlaying,
        currentTimeS: this.currentTimeS,
        durationS: duration,
        transportDisabled,
        bypass: this.bypass,
        pulse: !this.hasInteracted,
      },
      {
        onSeekStart: this.handleSeekStart,
        onTogglePlay: () => this.togglePlay(),
        onSeekEnd: this.handleSeekEnd,
        onToggleBypass: this.handleToggleBypass,
      },
    );

    const body =
      this.status === 'error'
        ? html`<div class="body">${renderErrorPanel(() => this.retry())}</div>`
        : html`
            <div class="body">
              <div class="left">
                <div class="wave-stack">
                  <canvas
                    class="ruler"
                    aria-hidden="true"
                    @pointerdown=${this.handleRulerPointerDown}
                    @pointermove=${this.handleRulerPointerMove}
                    @pointerup=${this.handleRulerPointerUp}
                    @pointercancel=${this.handleRulerPointerUp}
                  ></canvas>
                  <div class="wave-frame">
                    <canvas
                      class="waveform"
                      tabindex="0"
                      role="slider"
                      aria-label="Playback position"
                      aria-valuemin="0"
                      aria-valuemax=${Math.max(0, duration)}
                      aria-valuenow=${this.currentTimeS}
                      aria-valuetext=${`${this.currentTimeS.toFixed(1)} of ${duration.toFixed(1)} seconds`}
                    ></canvas>
                    ${showLoadingOverlay
                      ? html`
                          <div class="waveform-loading" aria-live="polite">
                            <span class="spinner" aria-hidden="true"></span>
                            <span class="waveform-loading-label">Loading audio…</span>
                          </div>
                        `
                      : nothing}
                  </div>
                  <div class="playhead" aria-hidden="true">
                    <div
                      class="hit"
                      @pointerdown=${this.handleRulerPointerDown}
                      @pointermove=${this.handleRulerPointerMove}
                      @pointerup=${this.handleRulerPointerUp}
                      @pointercancel=${this.handleRulerPointerUp}
                    >
                      <div class="grip"></div>
                    </div>
                    <div class="line"></div>
                  </div>
                </div>
              </div>
              <div class="panel">
                <div class="file-row" title=${this.currentModel.clipName}>${this.currentModel.clipName}</div>
                <div class="divider"></div>
                ${renderModelPicker(
                  {
                    current: this.currentModel,
                    open: this.pickerOpen,
                    highlightId: this.pickerHighlight,
                    sections: menuSections(),
                    disabled: !this.hasBeenReady && this.status !== 'ready',
                  },
                  {
                    onToggle: this.handlePickerToggle,
                    onSelect: (id) => this.handleModelChange(id),
                    onHighlight: (id) => (this.pickerHighlight = id),
                    onKeydown: this.handlePickerKeydown,
                  },
                )}
                <div class=${classMap({ rows: true, bypassed: this.bypass })}>
                  ${this.renderRows()}
                </div>
              </div>
            </div>
          `;

    return html`
      <div class="too-narrow" role="img" aria-label="Photon demo requires a wider viewport">
        Photon demo requires a wider viewport.
      </div>
      <div class="card" part="card">
        ${nav}
        ${body}
      </div>
      <div class="sr-only" role="status" aria-live="polite">${this.announcement}</div>
    `;
  }

  private retry(): void {
    this.status = 'idle';
    this.startLoading();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'photon-widget': PhotonWidget;
  }
}
